import FormControl from "@material-ui/core/FormControl";
import InputAdornment from "@material-ui/core/InputAdornment";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import SearchIcon from "@material-ui/icons/Search";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { languages } from "../constants/languages";
import CreateTournamentContainer from "../containers/CreateTournamentContainer";
import TournamentListElement from "./TournamentListElement";

const styles = (theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    maxWidth: 800,
    margin: "0 auto",
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  controls: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: theme.spacing(2),
    },
  },
  searchField: {
    flexGrow: 1,
    width: "100%",
  },
  selectsContainer: {
    display: "flex",
    flexGrow: 1,
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  sortSelect: {
    flexGrow: 1,
    [theme.breakpoints.down("xs")]: {
      flex: 1,
    },
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  tournamentGrid: {
    marginTop: theme.spacing(2),
  },
  createButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
});

const TournamentList = (props) => {
  const { classes } = props;
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [filterBy, setFilterBy] = useState("all");

  const filterAndSortTournaments = (tournaments) => {
    let filtered = tournaments.filter((tournament) =>
      tournament.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Change props.filterBy to filterBy to use the local state
    filtered = filtered.filter((tournament) => {
      if (filterBy === "all") return true;
      if (filterBy === "shared") return tournament.shared;
      if (filterBy === "my") return !tournament.shared;
      return true;
    });

    switch (sortBy) {
      case "oldest":
        return filtered.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
      case "a-z":
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      case "z-a":
        return filtered.sort((a, b) => b.name.localeCompare(a.name));
      default: // newest
        return filtered.sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
    }
  };

  // Combine tournaments and sharedTournaments
  const allTournaments = [
    ...props.tournaments.map((t) => ({ ...t, shared: false })),
    ...props.sharedTournaments.map((t) => ({ ...t, shared: true })),
  ];

  return (
    <div className={classes.root}>
      {props.tournamentsLoaded && props.sharedTournamentsLoaded && (
        <>
          <div className={classes.header}>
            <Typography variant="h6">
              {languages.general.tournaments}
            </Typography>
            <CreateTournamentContainer
              user={props.user}
              userData={props.userData}
            />
          </div>

          <div className={classes.controls}>
            <TextField
              className={classes.searchField}
              placeholder={`${languages.general.searchTournament}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <div className={classes.selectsContainer}>
              {props.sharedTournaments.length > 0 && (
                <FormControl className={classes.sortSelect}>
                  <Select
                    value={filterBy}
                    onChange={(e) => setFilterBy(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="all">
                      {languages.general.tournamentFiltering.all}
                    </MenuItem>
                    <MenuItem value="my">
                      {languages.general.tournamentFiltering.my}
                    </MenuItem>
                    <MenuItem value="shared">
                      {languages.general.tournamentFiltering.shared}
                    </MenuItem>
                  </Select>
                </FormControl>
              )}

              <FormControl className={classes.sortSelect}>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="newest">
                    {languages.general.tournamentSorting.newest}
                  </MenuItem>
                  <MenuItem value="oldest">
                    {languages.general.tournamentSorting.oldest}
                  </MenuItem>
                  <MenuItem value="a-z">
                    {languages.general.tournamentSorting.aZ}
                  </MenuItem>
                  <MenuItem value="z-a">
                    {languages.general.tournamentSorting.zA}
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>

          <div className={classes.section}>
            <div>
              {filterAndSortTournaments(allTournaments).length > 0 ? (
                <div>
                  {filterAndSortTournaments(allTournaments).map(
                    (tournament) => (
                      <TournamentListElement
                        key={tournament[".key"]}
                        tournament={tournament}
                        handleTournamentClick={props.handleTournamentClick}
                        shared={tournament.shared}
                        userStatus={props.userStatus}
                        userData={props.userData}
                        user={props.user}
                      />
                    )
                  )}
                </div>
              ) : (
                <div className={classes.emptyState}>{languages.general.noTournamentsFound}</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

TournamentList.propTypes = {
  classes: PropTypes.object.isRequired,
  tournaments: PropTypes.array.isRequired,
  sharedTournaments: PropTypes.array.isRequired,
  tournamentsLoaded: PropTypes.bool.isRequired,
  sharedTournamentsLoaded: PropTypes.bool.isRequired,
  handleTournamentClick: PropTypes.func.isRequired,
  user: PropTypes.object,
  userData: PropTypes.object,
  userStatus: PropTypes.object,
};

export default withStyles(styles)(TournamentList);
